// Reactive store for My Bookmarks + slide hide/remove + "newly added"
// indicators. Persisted to localStorage so demo state survives reloads.
//
// First-init behavior: when there is no prior localStorage entry for the
// extension list (a fresh visitor), we seed it with the 10
// DEFAULT_FAVORITE_IDS so the favorites view isn't empty out of the box.

import Vue from 'vue'
import { DEFAULT_FAVORITE_IDS, type SlideType } from '../data/slideTypeCatalog'

const HIDDEN_KEY = 'aha:slide-picker-demo:hidden'
const EXTENSION_KEY = 'aha:slide-picker-demo:pickerExtension'
const NEWLY_ADDED_KEY = 'aha:slide-picker-demo:newlyAdded'

function loadList(key: string): string[] {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) as string[] : []
  } catch {
    return []
  }
}
function saveList(key: string, ids: string[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(ids))
  } catch { /* private mode etc. */ }
}

function seedExtensionsIfFirstVisit(): string[] {
  try {
    if (localStorage.getItem(EXTENSION_KEY) === null) {
      const seed = [...DEFAULT_FAVORITE_IDS]
      saveList(EXTENSION_KEY, seed)
      return seed
    }
  } catch { /* private mode — return whatever loadList gives */ }
  return loadList(EXTENSION_KEY)
}

export const pinnedStore = Vue.observable({
  hiddenIds: loadList(HIDDEN_KEY) as string[],
  pickerExtensionIds: seedExtensionsIfFirstVisit(),
  newlyAddedIds: loadList(NEWLY_ADDED_KEY) as string[],
  lastPickedId: null as string | null,
})

export function isNewlyAdded(id: string): boolean {
  return pinnedStore.newlyAddedIds.includes(id)
}
export function clearNewlyAdded(id: string): void {
  if (!isNewlyAdded(id)) return
  pinnedStore.newlyAddedIds = pinnedStore.newlyAddedIds.filter((p) => p !== id)
  saveList(NEWLY_ADDED_KEY, pinnedStore.newlyAddedIds)
}

export function isHidden(id: string): boolean { return pinnedStore.hiddenIds.includes(id) }

/**
 * "Is this slide in My Bookmarks?" — a user-curated state. The 10
 * defaults are seeded into pickerExtensionIds on first visit; after
 * that the user controls the list entirely.
 */
export function isInPicker(slide: SlideType): boolean {
  if (isHidden(slide.id)) return false
  return pinnedStore.pickerExtensionIds.includes(slide.id)
}

export function addSlideToPicker(slide: SlideType): void {
  if (isHidden(slide.id)) {
    pinnedStore.hiddenIds = pinnedStore.hiddenIds.filter((p) => p !== slide.id)
    saveList(HIDDEN_KEY, pinnedStore.hiddenIds)
  }
  if (!pinnedStore.pickerExtensionIds.includes(slide.id)) {
    pinnedStore.pickerExtensionIds = [...pinnedStore.pickerExtensionIds, slide.id]
    saveList(EXTENSION_KEY, pinnedStore.pickerExtensionIds)
  }
  if (!isNewlyAdded(slide.id)) {
    pinnedStore.newlyAddedIds = [...pinnedStore.newlyAddedIds, slide.id]
    saveList(NEWLY_ADDED_KEY, pinnedStore.newlyAddedIds)
  }
}

export function removeSlideFromPicker(slide: SlideType): void {
  pinnedStore.pickerExtensionIds = pinnedStore.pickerExtensionIds.filter((p) => p !== slide.id)
  saveList(EXTENSION_KEY, pinnedStore.pickerExtensionIds)
  clearNewlyAdded(slide.id)
}

export function flashPicked(id: string): void {
  pinnedStore.lastPickedId = id
}
